import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import { Avatar, type AvatarProps, type AvatarSize } from "./Avatar";

export interface AvatarGroupItem extends Pick<AvatarProps, "initials" | "src" | "alt" | "variant"> {
  key: string;
}

/** Mirrors Avatar's own SIZE_CLASSES pixel values, needed here to compute the interactive variant's hover geometry. */
const SIZE_PX: Record<AvatarSize, number> = { xs: 22, sm: 28, md: 36, lg: 44 };
/** Matches the -space-x-1 overlap used on the avatar stack itself. */
const OVERLAP_PX = 4;

interface AvatarGroupBaseProps {
  avatars: AvatarGroupItem[];

  size?: AvatarSize;

  max?: number;

  /** An extra avatar-shaped node appended after the group (e.g. a non-human identity avatar), overlapping like the rest. */
  trailingAvatar?: ReactNode;
}

export type AvatarGroupProps =
  | (AvatarGroupBaseProps & { onClick?: undefined })
  | (AvatarGroupBaseProps & { onClick: () => void; "aria-label": string });

export function AvatarGroup(props: Readonly<AvatarGroupProps>) {
  const { avatars, size = "sm", max, trailingAvatar } = props;
  const visible = max ? avatars.slice(0, max) : avatars;
  const showCount = max !== undefined && avatars.length > 0;

  const stack = (
    <span className="flex items-center -space-x-1">
      {visible.map((a, index) => (
        <span
          key={a.key}
          style={{ zIndex: visible.length - index }}
          className="relative inline-flex rounded-full ring-2 ring-surface-elevated"
        >
          <Avatar initials={a.initials} src={a.src} alt={a.alt} variant={a.variant} size={size} />
        </span>
      ))}
      {trailingAvatar && (
        <span className="relative inline-flex shrink-0 rounded-full ring-2 ring-surface-elevated">
          {trailingAvatar}
        </span>
      )}
    </span>
  );

  if (props.onClick) {
    const avatarCount = visible.length + (trailingAvatar ? 1 : 0);
    const lastAvatarCenter = (avatarCount - 1) * (SIZE_PX[size] - OVERLAP_PX) + SIZE_PX[size] / 2;

    return (
      <button
        type="button"
        onClick={props.onClick}
        aria-label={props["aria-label"]}
        className="group relative isolate flex cursor-pointer items-center gap-1 py-0.5 pr-1.5"
      >
        <span
          aria-hidden="true"
          style={{ left: lastAvatarCenter }}
          className="-z-10 absolute inset-y-0.5 right-0 rounded-r-full transition-colors group-hover:bg-surface-secondary"
        />
        {stack}
        <ChevronDown
          size={14}
          className="shrink-0 text-content-tertiary transition-colors group-hover:text-content-primary"
        />
      </button>
    );
  }

  return (
    <span className="inline-flex items-center">
      {stack}
      {showCount && <span className="pl-1.5 text-xs tabular-nums text-content-tertiary">{avatars.length}</span>}
    </span>
  );
}
